import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react'
import { useImmer } from 'use-immer'

const STORAGE_KEY_SEARCH_HISTORY = 'searchWordsHistory'

interface SearchHistoryContextValue {
  add: (scope: string, item: string) => void
  remove: (scope: string, item: string) => void
  clear: (scope: string) => void
  get: (scope: string, item?: string) => string[]
  history: Record<string, string[]>
}

const historyContext = createContext<SearchHistoryContextValue>({
  add: (scope, item) => {},
  remove: (scope, item) => {},
  clear: (scope) => {},
  get: (scope) => [],
  history: {},
})

export const useSearchHistory = (): SearchHistoryContextValue => {
  return useContext(historyContext)
}

interface SearchHistoryProviderProps {
  children: React.ReactNode
}

export const SearchHistoryProvider = ({ children }: SearchHistoryProviderProps): React.ReactElement => {
  const [history, updateHistory] = useImmer<Record<string, string[]>>(() => load())

  const add = useCallback(
    (scope: string, item: string) => {
      const list = history[scope] ?? []
      const newList = [...new Set([item, ...list])]
      updateHistory((draft) => {
        draft[scope] = newList
      })
    },
    [history, updateHistory]
  )

  const remove = useCallback(
    (scope: string, item: string) => {
      const list = history[scope] ?? []
      const set = new Set(list)
      set.delete(item)
      const newList = [...set]
      updateHistory((draft) => {
        draft[scope] = newList
      })
    },
    [updateHistory, history]
  )

  const clear = useCallback(
    (scope: string) => {
      updateHistory((draft) => {
        draft[scope] = []
      })
    },
    [updateHistory]
  )

  const get = useCallback(
    (scope: string, item?: string) => {
      const list = history[scope] ?? []
      return list
    },
    [history]
  )

  function save(history: Record<string, string[]>) {
    const json = JSON.stringify(history)
    window?.localStorage?.setItem(STORAGE_KEY_SEARCH_HISTORY, json)
  }

  function load(): Record<string, string[]> {
    const history = window?.localStorage?.getItem(STORAGE_KEY_SEARCH_HISTORY)
    if (!history) return {}
    try {
      const result = JSON.parse(history)
      return result && typeof result == 'object' ? result : {}
    } catch {
      return {}
    }
  }

  useEffect(() => {
    save(history)
  }, [history])

  const state = useMemo(
    () => ({
      add,
      remove,
      clear,
      get,
      history,
    }),
    [add, clear, get, history, remove]
  )

  return <historyContext.Provider value={state}>{children}</historyContext.Provider>
}
